import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import FaqService from '~/services/FaqService';
import type { Faq, FaqCategory, FaqsByCategoryResponse } from '~/types/api';

/**
 * Sık Sorulan Sorular verilerini yönetmek için composable
 */
export function useFaq() {
  const { locale } = useI18n();
  
  // State değişkenleri
  const categories = ref<FaqCategory[]>([]);
  const faqs = ref<Faq[]>([]);
  const selectedCategory = ref<number | null>(null);
  const categoryDetails = ref<{ id: number; title: string; description: string } | null>(null);
  
  const isLoadingCategories = ref<boolean>(false);
  const isLoadingFaqs = ref<boolean>(false);
  const error = ref<string | null>(null);
  
  // FAQ servisi
  const faqService = FaqService.getInstance();
  
  // Kategori yükleme
  const fetchCategories = async () => {
    isLoadingCategories.value = true;
    error.value = null;
    
    try {
      const data = await faqService.getActiveCategories(locale.value);
      categories.value = data;
      
      // İlk kategoriyi seç (varsa)
      if (data.length > 0 && selectedCategory.value === null) {
        selectedCategory.value = data[0].id;
        fetchFaqsByCategory(data[0].id);
      }
    } catch (err) {
      console.error('SSS kategorileri yüklenirken hata oluştu:', err);
      error.value = 'SSS kategorileri yüklenirken bir hata oluştu.';
    } finally {
      isLoadingCategories.value = false;
    }
  };
  
  // Tüm SSS'leri yükleme
  const fetchAllFaqs = async () => {
    isLoadingFaqs.value = true;
    error.value = null;
    
    try {
      const data = await faqService.getAllFaqs(locale.value);
      faqs.value = data;
      // Kategori detaylarını temizle
      categoryDetails.value = null;
      selectedCategory.value = null;
    } catch (err) {
      console.error('SSS listesi yüklenirken hata oluştu:', err);
      error.value = 'SSS listesi yüklenirken bir hata oluştu.';
    } finally {
      isLoadingFaqs.value = false;
    }
  };
  
  // Kategoriye göre SSS'leri yükleme
  const fetchFaqsByCategory = async (categoryId: number) => {
    if (!categoryId) return;
    
    isLoadingFaqs.value = true;
    error.value = null;
    selectedCategory.value = categoryId;
    
    try {
      const response = await faqService.getFaqsByCategory(categoryId, locale.value);
      
      if (response) {
        faqs.value = response.data;
        categoryDetails.value = response.category;
      } else {
        faqs.value = [];
        error.value = 'Kategoriye ait SSS bulunamadı.';
      }
    } catch (err) {
      console.error(`Kategori ID: ${categoryId} için SSS'ler yüklenirken hata oluştu:`, err);
      error.value = 'SSS listesi yüklenirken bir hata oluştu.';
      faqs.value = [];
    } finally {
      isLoadingFaqs.value = false;
    }
  };
  
  // SSS'leri kategorilere göre gruplandır
  const faqsByCategory = computed(() => {
    const grouped: Record<number, Faq[]> = {};
    
    faqs.value.forEach(faq => {
      if (!grouped[faq.faq_category_id]) {
        grouped[faq.faq_category_id] = [];
      }
      grouped[faq.faq_category_id].push(faq);
    });
    
    return grouped;
  });
  
  // Component oluşturulduğunda kategorileri yükle
  onMounted(() => {
    fetchCategories();
  });
  
  // Dil değiştiğinde yeniden yükle
  watch(() => locale.value, () => {
    fetchCategories();
    if (selectedCategory.value) {
      fetchFaqsByCategory(selectedCategory.value);
    } else {
      fetchAllFaqs();
    }
  });
  
  return {
    categories,
    faqs,
    selectedCategory,
    categoryDetails,
    isLoadingCategories,
    isLoadingFaqs,
    error,
    faqsByCategory,
    fetchCategories,
    fetchAllFaqs,
    fetchFaqsByCategory
  };
} 